import { RadioGroup } from '@headlessui/react';
import { useProviderIcon } from 'components/integrations/useProviderIcon';
import { CardBackground } from 'components/ui';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { useGetStoreInfoQuery } from 'store/services/segmentsApi';
import { useParseURLQueryParams } from 'utils';
import { Provider } from './descriptions';
import { StepsLayout } from './StepsLayout';
import { useOnboardingState } from './useOnboardingState';

export default function IntegrationsStep() {
  const location = useLocation();

  const {
    setProviders,
    providerSearch,
    setProviderSearch,
    categoryFilter,
    setCategoryFilter,
    filteredProviders,
    localStorageKey,
  } = useOnboardingState();

  return (
    <StepsLayout>
      <div className="mx-auto mt-4 lg:mt-10 lg:grid lg:grid-cols-8 lg:gap-x-9">
        <div className="col-span-3">
          <div className="px-2.5 lg:px-0">
            <p className="text-black text-opacity-30">4/4</p>
            <p className="mt-4 font-bold leading-8 text-segments-32px">
              Add integration
            </p>

            <div className="mt-4">
              <p>
                Segments enables you to centralize customer segmentation and
                sync across all your marketing channels, like email, SMS, paid
                ads and more. Set up your data sources and start delivering a
                consistent brand voice across all your channels.
              </p>
            </div>
          </div>
        </div>
        <div className="col-span-5 mt-10 lg:mt-0">
          <CardBackground>
            <div className="py-[30px] px-5 lg:p-10 h-[630px]">
              {/* <p className="text-2xl">Connect your providers</p> */}

              <div className="space-y-6 lg:space-y-0 lg:flex lg:items-end lg:justify-between">
                <div>
                  <input
                    type="text"
                    value={providerSearch}
                    onChange={(e) => setProviderSearch(e.target.value)}
                    placeholder="search provider..."
                    className="bg-[#EFEFEF] border-none rounded placeholder-opacity-50 px-4 tracking-wide"
                  />
                </div>

                <CategoryFilter
                  search={providerSearch}
                  category={categoryFilter}
                  setCategory={setCategoryFilter}
                />
              </div>

              <div className="mt-6 space-y-4 overflow-y-auto lg:mt-10 pr-4 h-[418px] lg:h-[85%] scrollbar-thin scrollbar-thumb-segments-darker-gray-75 scrollbar-thumb-rounded-md">
                {filteredProviders.map((provider, index) => (
                  <ProviderCard
                    provider={provider}
                    updateProvider={setProviders}
                    key={index}
                  />
                ))}
              </div>
            </div>
          </CardBackground>
        </div>
      </div>

      <div className="flex items-center justify-center my-10 space-x-8 ">
        <Link
          to={{
            ...location,
            pathname: '/onboarding/recommended_segments',
          }}
          className="py-3 font-bold text-black text-opacity-50 lg:px-14 rounded-xl "
        >
          Previous
        </Link>
        <Link
          onClick={() => {
            localStorage.removeItem(localStorageKey);
          }}
          to={{
            ...location,
            pathname: '/dashboard/welcome',
          }}
          className="py-3 font-bold text-white border px-14 lg:px-20 rounded-xl bg-segments-accent-color-1 border-segments-accent-color-1"
        >
          Go to Dashboard
        </Link>
      </div>
    </StepsLayout>
  );
}

function ProviderCard({
  provider,
  updateProvider,
}: {
  provider: Provider;
  updateProvider: React.Dispatch<React.SetStateAction<Provider[]>>;
}) {
  const location = useLocation();
  const { ProviderIcon } = useProviderIcon({ provider: provider.name });

  const isProviderConnected = provider.connected;

  const { rawQueryParams } = useParseURLQueryParams();

  const queryParams = `store_name=${rawQueryParams?.store_name}`;

  const { isFetching } = useGetStoreInfoQuery(queryParams, {
    skip: !!!rawQueryParams?.store_name,
  });

  return (
    <Link
      to={{
        pathname: `/integrations/connect/${provider.name}`,
        state: { background: location },
        search: location.search,
      }}
      className={`p-4 border lg:flex lg:items-center lg:justify-between rounded-segments-10px ${
        (isFetching || isProviderConnected) && 'pointer-events-none opacity-50'
      }`}
    >
      <div className="flex items-start space-x-4 lg:items-center ">
        <div>{ProviderIcon}</div>
        <div>
          <p className="font-bold">{provider.name}</p>
          <p className="text-black text-opacity-50">{provider.description}</p>
        </div>
      </div>

      <div className="mt-4 pl-11 lg:0 lg:mt-0">
        <div
          className={`flex px-5 py-2.5 font-bold  bg-segments-accent-color-1 text-white rounded-2xl  space-x-2 `}
        >
          {isProviderConnected ? <p>Connected</p> : <p>Connect</p>}
        </div>
      </div>
    </Link>
  );
}

function CategoryFilter({
  category,
  search,
  setCategory,
}: {
  category: string;
  search: string;
  setCategory: any;
}) {
  const categories = ['Recomended', 'All', 'Email', 'Ads', 'SMS'];

  return (
    <RadioGroup
      value={search.length > 0 ? '' : category}
      onChange={setCategory}
      className={`flex lg:pr-4 space-x-1.5 lg:space-x-2 text-segments-link text-segments-13px cursor-pointer ${
        search.length > 0 && 'opacity-30'
      }`}
      disabled={search.length > 0}
    >
      {categories.map((option) => (
        <RadioGroup.Option value={option.toLowerCase()} key={option}>
          {({ checked }) => (
            <span
              className={
                checked
                  ? ' px-2.5 lg:px-3 py-1 font-bold text-white border rounded-full bg-segments-accent-color-1 border-segments-accent-color-bg-segments-accent-color-1'
                  : ' px-2.5 lg:px-3 py-1 border rounded-full border-segments-link'
              }
            >
              {option}
            </span>
          )}
        </RadioGroup.Option>
      ))}
    </RadioGroup>
  );
}
